import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild
} from "@angular/core";
import { ConfService, NoteService, SearchService, Store } from "@viewer/core";
import { TitleService } from "@viewer/header/title.service";
import { IReactionDisposer, reaction } from "mobx";
import { MatDialog } from "@angular/material/dialog";
import { WebTekDialogComponent } from "@viewer/toc-items/note-module/webtek-dialog/web-tek-dialog.component";
import { Router } from "@angular/router";
import { ApplicabilityService } from "@viewer/core/applicability/applicability.service";
import { SearchResult } from "@viewer/core/search/searchModel";
import { AttachedMedia, CommentLocation, Note, TocItemScope } from "@orion2/models/tocitem.models";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  ConfirmDialogResult
} from "libs/components/confirm-dialog/confirm-dialog.component";
import { TranslateService } from "@ngx-translate/core";
import { MessageService } from "@orion2/message-service/message.service";
import { cloneDeep } from "lodash";
import { ALLOWED_EXTENSIONS_MIME_TYPES } from "@orion2/utils/constants.utils";
import { CommentService } from "@viewer/toc-items/comments-module/services/comment.service";
import { getMajorRevision } from "@orion2/utils/functions.utils";

@Component({
  standalone: false,
  selector: "o-note",
  templateUrl: "./note.component.html",
  styleUrls: ["./note.component.scss"]
})
export class NoteComponent implements OnInit, OnDestroy {
  public readonly noteService = inject(NoteService);

  @Input() item: Note;
  @ViewChild("fileInput")
  fileInput: ElementRef;
  @ViewChild("titleInput")
  titleInput: ElementRef;
  @HostBinding("class.not-applicable") isNotApplicable = false;
  @HostBinding("class.hide-not-applicable") hideNotApplicable = false;
  @HostBinding("class.inline")
  get isInline(): boolean {
    return (
      this.noteService.selectAnchorMode() && this.item._id === this.noteService.editingNote()?._id
    );
  }
  @HostBinding("attr.id")
  get hostId(): string | null {
    return this.item._id;
  }

  public currentNote: Note;
  public availableStatus = [];
  public editMode = signal(false);
  public saved: boolean;
  public canOpenMenu = false;
  public webtek = signal(false);
  public showMediaPanel = false;
  public totalMediaSize: number;
  public newMediaSize: number;
  public canTransformIntoComment = false;

  public readonly ALLOWED_MIME_TYPES_LIST = Array.from(ALLOWED_EXTENSIONS_MIME_TYPES.values()).join(
    ", "
  );

  public readonly hasWarning = computed<boolean>(() =>
    this.noteService.noteWarningIds().has(this.item?._id)
  );

  private disposers: IReactionDisposer[] = [];
  private searchData: SearchResult;
  private status: TocItemScope[] = [
    TocItemScope.PUBLIC,
    TocItemScope.CORPORATE,
    TocItemScope.PRIVATE
  ];

  constructor(
    public store: Store,
    public titleService: TitleService,
    public confService: ConfService,
    private readonly searchService: SearchService,
    private matDialog: MatDialog,
    private router: Router,
    private cd: ChangeDetectorRef,
    private applicabilityService: ApplicabilityService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private messageService: MessageService,
    private commentService: CommentService
  ) {
    effect(() => {
      if (!this.store.pubInfo?.capabilities?.notesInline) {
        return;
      }
      if (this.editMode()) {
        this.noteService.editingNote.set(this.currentNote);
        this.noteService.selectedAnchor = this.currentNote.anchor?.id;
        this.noteService.noteToScroll = this.currentNote;
      } else {
        this.noteService.editingNote.set(undefined);
      }
    });
  }

  ngOnInit() {
    this.item.date = new Date(this.item.date);
    this.item.medias ??= [];
    this.canTransformIntoComment = !/preprint|sb|document/.test(this.item.dmc);
    // this is for set focus when we create a note with drawer open
    setTimeout(() => {
      this.titleInput?.nativeElement?.focus();
    }, 0);
    this.webtek.set(
      !this.confService.isOnPrem && this.store.pubInfo.capabilities.webtek && !this.store.isGov
    );

    this.editMode.set(this.isNoteEmpty());
    this.currentNote = cloneDeep(this.item);
    this.totalMediaSize = this.getMediaSize();
    this.newMediaSize = 0;

    // When we create an empty note (editMode === true), if "this.saved = true", the user won't be warned about leaving the page
    // without saving (see desactivate guard)
    // Each note is registered in the note.service which keeps track of the saved and unsaved notes (see "saveAllNotes()").
    this.saved = !this.editMode();
    this.noteService.addNoteComponent(this);
    this.updateSelect();
    this.updateCanOpenMenu();

    this.disposers.push(
      reaction(
        () => this.store.user,
        () => {
          this.updateSelect();
          this.updateCanOpenMenu();
          this.cd.detectChanges();
        }
      )
    );

    // If the search isn't ready we can't get duMetadata when it is ready we can get the datas
    this.disposers.push(
      reaction(
        () => this.store.offlineSearchReady,
        () => {
          if (this.store.offlineSearchReady) {
            this.searchData = this.store.getDUMeta(this.currentNote.dmc);
            this.isNotApplicable = !this.applicabilityService.isApplicable(this.searchData);
          }
        },
        {
          fireImmediately: true
        }
      )
    );

    this.disposers.push(
      reaction(
        () => ({
          applicMd5: this.store.applicableMD5,
          show: this.store.showNotApplicable,
          isFilteringAvailable: this.store.isFilteringAvailable
        }),
        () => {
          const data = this.searchData || this.currentNote;
          this.isNotApplicable = !this.applicabilityService.isApplicable(data);
          this.hideNotApplicable = !this.store.showNotApplicable;
        },
        { fireImmediately: true }
      )
    );
  }

  ngOnDestroy(): void {
    this.noteService.removeNoteComponent(this);
    this.disposers.forEach(disposer => disposer());
  }

  public onchange() {
    this.saved = false;
  }

  /**
   * Manage Note card control with user rights
   */
  private updateSelect() {
    this.availableStatus = [];
    if (this.store.user) {
      this.availableStatus = this.status.filter(status =>
        this.noteService.userHasRightForNoteStatus(status)
      );

      if (!this.store.user.makePrivateNote) {
        this.desactivateNoteCardCursor();
      } else {
        this.activateNoteCardCursor();
      }
    } else {
      this.editMode.set(false);
      const shouldRemoveNote = this.isNoteEmpty();

      if (shouldRemoveNote) {
        this.cancel();
      } else {
        this.saveNote(this.item);
      }
      this.desactivateNoteCardCursor();
    }
    this.cd.detectChanges();
  }

  /**
   * Activates the menu if the user has the rights to edit/delete...
   *
   * @memberof NoteComponent
   */
  public updateCanOpenMenu(): void {
    this.canOpenMenu = this.noteService.userHasRightForNoteStatus(this.currentNote.status);
  }

  /**
   * On click to "fileInput" button
   * Update the currentNote
   * @memberof NoteComponent
   */
  public async addMedia(event: Event): Promise<void> {
    if (!this.noteService.canAttachMedia) {
      return;
    }
    const file = (event.target as HTMLInputElement).files[0];
    (event.target as HTMLInputElement).value = "";
    try {
      this.currentNote = await this.noteService.attachMediaToNote(file, this.currentNote);
      this.newMediaSize += file.size;
    } catch (error) {
      this.messageService.error(error.message);
    }
  }

  /**
   * Remove the media from the currentNote
   * @memberof NoteComponent
   */
  public removeMedia(media: AttachedMedia): void {
    if (!this.noteService.canAttachMedia) {
      return;
    }
    const index = this.currentNote.medias.indexOf(media);
    this.currentNote.medias.splice(index, 1);
    this.newMediaSize -= media.size;
  }

  /**
   * Open the media in a new tab for the browser app.
   * Download the media for the cordova and electron apps.
   * @param event
   * @param mediaId
   * @param fileName
   */
  public openMedia(event: Event, mediaId: string, fileName: string): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.noteService.canAttachMedia) {
      return;
    }
    this.noteService.openMedia(mediaId, fileName, this.currentNote.status);
  }

  /**
   * Computes the sum of medias attached to a note
   * @memberof NoteComponent
   */
  private getMediaSize(): number {
    return this.currentNote.medias.reduce(
      (totalSize: number, noteMedia: AttachedMedia) => totalSize + noteMedia.size,
      0
    );
  }

  /**
   * Open and close the medias panel
   * @memberof NoteComponent
   */
  public toggleMediaPanel(event: Event): void {
    if (!this.noteService.canAttachMedia) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.showMediaPanel = !this.showMediaPanel;
  }

  /**
   * Activates edition mode if the user has the rights.
   *
   * @memberof NoteComponent
   */
  public startEdit(): void {
    this.editMode.set(this.noteService.userHasRightForNoteStatus(this.currentNote.status));
    this.newMediaSize = 0;
    setTimeout(() => {
      this.titleInput?.nativeElement?.focus();
    });
  }

  /**
   * Confirm the current note position.
   * @see noteService for more details.
   */
  public async confirmPosition(): Promise<void> {
    this.noteService.confirmPosition(this.currentNote);
  }

  private isNoteEmpty(): boolean {
    return !this.item.customTitle && !this.item.content;
  }

  /**
   * Activates the cursor style: pointer.
   */
  private activateNoteCardCursor() {
    this.setNoteCardCursor("pointer");
  }

  /**
   * Deactivates the cursor style.
   */
  private desactivateNoteCardCursor() {
    this.setNoteCardCursor();
  }

  /**
   * Sets the cursor style.
   *
   * @param style the style to apply. The default is "default".
   */
  private setNoteCardCursor(style = "default"): void {
    document
      .querySelectorAll("form, textarea, input")
      .forEach((element: HTMLElement) => (element.style.cursor = style));
  }

  /**
   * Navigate to DU
   *
   * @returns
   * @memberOf NoteComponent
   */
  public navigateToDU() {
    if (this.allowNavigation()) {
      this.noteService.noteToScroll = this.item;
      return this.router.navigate([
        "pub",
        this.store.publicationID,
        this.store.publicationRevision,
        "du",
        this.item.dmc
      ]);
    }
  }

  /**
   * Indicates whether or not the note is in a state when a navigation on click is allowed
   *
   * @returns The result of the test
   * @memberOf NoteComponent
   */
  private allowNavigation(): boolean {
    return !this.editMode() && this.titleService.headerTitle === "notes.label";
  }

  /**
   * Save current note and keep the previous one
   *
   * @param newNote
   * @memberOf NoteComponent
   */
  public saveNote(newNote: Note): Promise<boolean> {
    this.editMode.set(false);
    this.saved = true;
    newNote.author = this.store.user.userName;
    if (
      this.store.pubInfo?.capabilities?.notesInline &&
      newNote.status === TocItemScope.CORPORATE
    ) {
      newNote.anchor = {
        id: this.noteService.selectedAnchor,
        lastCheckRevision: getMajorRevision(this.store.publicationRevision),
        lastAnchorUpdate: Date.now()
      };
    }
    const usedMsBefore = this.store.mediaStorageUsed;

    if (newNote.medias) {
      newNote.medias = this.deduplicateTitles(newNote);
    }
    // keep previous note
    return this.noteService.updateNote(this.item, newNote).then((success: boolean) => {
      if (!success) {
        return false;
      }
      // SPEC: Update mediaStorageUsed only if not already updated
      if (
        this.item.status === TocItemScope.PRIVATE &&
        usedMsBefore === this.store.mediaStorageUsed
      ) {
        this.store.mediaStorageUsed += this.newMediaSize;
      }

      this.editMode.set(false);
      this.currentNote = cloneDeep(newNote);
      this.totalMediaSize = this.getMediaSize();
      this.searchService.updateSearch();
      return true;
    });
  }

  /**
   * Delete note and emit event to list
   *
   * @memberOf NoteComponent
   */
  public delete(): void {
    const data: ConfirmDialogData = {
      message: [
        this.translate.instant("folders.delete", {
          name: this.currentNote.customTitle
        })
      ],
      confirmLabels: [this.translate.instant("continue")],
      cancelLabels: [this.translate.instant("cancel")]
    };

    this.dialog
      .open(ConfirmDialogComponent, { data })
      .afterClosed()
      .subscribe((result: ConfirmDialogResult) => {
        if (result.confirm) {
          const usedMsBefore = this.store.mediaStorageUsed;
          return this.noteService.deleteNote(this.currentNote).then((res: boolean) => {
            this.searchService.updateSearch();
            this.noteService.removeNoteComponent(this);
            this.saved = res;

            // SPEC: Update mediaStorageUsed only if not already updated
            if (
              this.currentNote.status === TocItemScope.PRIVATE &&
              usedMsBefore === this.store.mediaStorageUsed
            ) {
              this.store.mediaStorageUsed -= this.currentNote.medias?.reduce(
                (cur, media: AttachedMedia) => cur + media.size,
                0
              );
            }
          });
        }
      });
  }

  /**
   * Don't save and return to the previous note
   *
   * @param event - The triggering event (if any)
   * @memberOf NoteComponent
   */
  cancel(event?: Event) {
    // If it comes from the 'cancel' button we stop the propagation to avoid navigating to the DU
    if (event) {
      event.stopPropagation();
    }
    this.editMode.set(false);
    this.currentNote = cloneDeep(this.item);
    // we delete the current note if it's empty (no title)
    if (!this.item.customTitle) {
      return this.noteService.deleteNote(this.item).then((res: boolean) => {
        this.saved = res;
      });
    } else {
      this.saved = true;
    }
    this.showMediaPanel = false;
  }

  /**
   * Open the modal that pushes a note to WebTek
   *
   * @memberOf NoteComponent
   */
  public exportToWebTek(): void {
    if (!this.webtek()) {
      return;
    }
    this.matDialog.open(WebTekDialogComponent, {
      panelClass: "webTek-dialog-container",
      data: { note: this.item }
    });
  }

  /**
   * Creates the URL to access the created technical event
   *
   * @returns The URL
   * @memberOf NoteComponent
   */
  private linkWebTekURL(): string {
    return `${this.confService.conf.webTekUrl}/${this.item.technicalEvent.id}`;
  }

  /**
   * Open the URL using the system default browser
   */
  public openURL() {
    window.open(this.linkWebTekURL(), "_system");
  }

  /**
   * Transform the note into a new Comment
   */
  public transformIntoComment(): void {
    if (this.allowNavigation()) {
      this.router.navigate([
        "pub",
        this.store.pubInfo.packageId,
        this.store.pubInfo.revision,
        "comments"
      ]);
    }
    this.store.isCommentsPanelOpen = true;

    this.commentService.initComment(CommentLocation.DM, this.currentNote);
  }
  /**
   * update note in edition
   *
   * @memberOf NoteComponent
   */
  public onStatusChange(): void {
    if (!this.store.pubInfo?.capabilities?.notesInline) {
      return;
    }
    this.noteService.editingNote.set(undefined);
    this.noteService.editingNote.set(this.currentNote);
  }

  /**
   * Deduplicates notes media titles by adding a numeric suffix to titles that appear more than once.
   * @param note - The note containing the media list to process.
   * @returns Array of `AttachedMedia` without duplicate titles.
   *
   * @example
   * // Input:  ["test.png", "test.png", "test.png"]
   * // Output: ["test.png", "test_2.png", "test_3.png"]
   */
  private deduplicateTitles(note: Note): AttachedMedia[] {
    const titleCount = {};
    return note.medias.map((media: AttachedMedia) => {
      const title = media.title;
      titleCount[title] = (titleCount[title] || 0) + 1;
      if (titleCount[title] > 1) {
        const extensionIndex = title.lastIndexOf(".");
        return {
          ...media,
          title: `${title.slice(0, extensionIndex)}_${titleCount[title]}${title.slice(extensionIndex)}`
        };
      }
      return media;
    });
  }
}
